import { put, call, take, race, select, actionChannel } from "redux-saga/effects";
import { buffers } from "redux-saga";

import { qtyGridActions } from "actions/StockModal/qtyGridActions.js";
import {
  addStockModalItem,
  buyplanItemActions
} from "actions/likeDislikeActions";
import { getStockModalBuyplanTotals } from "actions/buyplanTotalsActions.js";

import { GET_DATA } from "actions/apiOperations.js";

import { putJSON } from "apis/apiHelpers.js";
import sharedApiSaga from "sagas/sharedApiSaga.js";

import { findByProductReleaseId } from "selectors/productsSelector.js";
import { buyplanDetailsDataSelector } from "selectors/buyplanDetailsSelector.js";
import { debounceNotification } from "utils/debounceNotification";
import { ATS_STATUSES } from "constants.js";
import i18next from "i18next";

export const requestQtyGridAsync = function*(action) {
  const {
    stockModalId,
    stockModalItemId,
    productReleaseId,
    ...params
  } = action.payload;

  if (
    stockModalItemId &&
    stockModalItemId !== "undefined" &&
    stockModalItemId !== "null"
  ) {
    yield put({
      type: GET_DATA,
      path: `/api/stock_models/${stockModalId}/items/${stockModalItemId}/qty_grid.json`,
      actions: {
        startLoader: qtyGridActions.startLoader,
        success: qtyGridActions.qtyGridSuccess,
        failure: qtyGridActions.qtyGridFailure
      },
      params
    });
  } else {
    yield put(addStockModalItem(stockModalId, productReleaseId));

    const { success } = yield race({
      success: take(buyplanItemActions.success),
      error: take(buyplanItemActions.failure)
    });

    if (success) {
      const productRelease = yield select(
        findByProductReleaseId(productReleaseId)
      );
      const stockModalItemId = productRelease.stock_model_item.id;

      yield put({
        type: GET_DATA,
        path: `/api/stock_models/${stockModalId}/items/${stockModalItemId}/qty_grid.json`,
        actions: {
          startLoader: qtyGridActions.startLoader,
          success: qtyGridActions.qtyGridSuccess,
          failure: qtyGridActions.qtyGridFailure
        },
        params
      });
    }
  }
};

export const requestUpdateQtyAsync = function*(action) {
  const {
    stockModalId,
    sizeBreakId,
    sizeBreakKey,
    sizeBreakSize,
    options,
    ...params
  } = action.payload;

  const result = yield call(
    sharedApiSaga,
    putJSON,
    `/api/stock_models/${stockModalId}/size_breaks/${sizeBreakId}/update_quantity.json`,
    {
      success: qtyGridActions.updateQtySuccess,
      failure: qtyGridActions.updateQtyFailure
    },
    { ...params, metadata: { sizeBreakKey, sizeBreakSize } }
  );

  if (result && result.apiResponse) {
    const {
      ats_status: atsStatus,
      order_qty_block: orderQtyBlock,
    } = result.apiResponse.data;

    switch (atsStatus) {
      case ATS_STATUSES.overQty:
        let exceededQtyloclisationData = i18next.t(
          "qtyGrid.sizeBreak.inputValidations.outOfRange"
        );
        debounceNotification({
          type: "error",
          message: exceededQtyloclisationData
        });
        break;
      case ATS_STATUSES.orderQtyBlock:
        let orderQtyBlockloclisationData = i18next.t(
          "qtyGrid.sizeBreak.inputValidations.qtyMultiplesMismatch",
          { orderQtyBlock: orderQtyBlock }
        );
        debounceNotification({
          type: "error",
          message: orderQtyBlockloclisationData
        });
        break;
      case ATS_STATUSES.sizeMinOrderQty:
        let sizeMinQtyloclisationData = i18next.t(
          "qtyGrid.sizeBreak.inputValidations.orderMinQtyMismatch"
        );
        debounceNotification({
          type: "error",
          message: sizeMinQtyloclisationData
        });
        break;
      case ATS_STATUSES.cumulativeOverQty:
        let cumulativeOverQtyloclisationData = i18next.t(
          "qtyGrid.sizeBreak.inputValidations.cumulativeExceededQty"
        );
        debounceNotification({
          type: "error",
          message: cumulativeOverQtyloclisationData
        });
        break;
      default:
      // do nothing
    }

    if (options.shouldFetchTotals) {
      const stockModelBuyplanItems = yield select(buyplanDetailsDataSelector);
      const stockModelBuyplanItemIds = stockModelBuyplanItems
        .get("stock_model_items")
        .map(key => key.get("id"))
        .toArray();

      yield put(
        getStockModalBuyplanTotals(stockModalId, stockModelBuyplanItemIds)
      );
    }
  }
};

// Tracks all in-flight or queued quantity updates and registers a beforeunload
// listener so the browser prompts the user before refreshing while saves are
// still pending. pendingCount covers both actions buffered in the actionChannel
// and the one currently being processed, because both have been dispatched to
// the store (and therefore seen by this watcher) but have not yet received a
// success/failure response.
export const watchPendingUpdates = function*() {
  let pendingCount = 0;

  const handleBeforeUnload = (evt) => {
    if (pendingCount > 0) {
      evt.preventDefault();
      evt.returnValue = '';
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  while (true) {
    const action = yield take([
      qtyGridActions.requestUpdateQty,
      qtyGridActions.updateQtySuccess,
      qtyGridActions.updateQtyFailure
    ]);

    if (action.type === qtyGridActions.requestUpdateQty) {
      pendingCount++;
    } else {
      pendingCount = Math.max(0, pendingCount - 1);
    }
  }
};

// Both requestQtyGrid and requestUpdateQty are processed through the same
// sequential channel. This ensures that a grid refresh (requestQtyGrid) always
// waits for all queued quantity saves to complete before fetching fresh data
// from the server, preventing stale values appearing after rapid in-app refreshes.
export const watchRequestUpdateQty = function*() {
  const channel = yield actionChannel(
    [qtyGridActions.requestUpdateQty, qtyGridActions.requestQtyGrid],
    buffers.expanding()
  );
  while (true) {
    const action = yield take(channel);
    if (action.type === qtyGridActions.requestQtyGrid) {
      yield call(requestQtyGridAsync, action);
    } else {
      yield call(requestUpdateQtyAsync, action);
    }
  }
};
